import qs from 'qs';
import axios from 'axios';
import get from 'lodash/get';

import { getInstance as getFlags } from 'utils/flags';

import uploadFile from './uploadFile';
import { getEventId, METHOD_POST, CONTENT_TYPE_FORM, catchSplajaxError } from './utils';

const flags = getFlags();

/**
 * Client for wrapping requests to Cake's backend.
 * Since every endpoint has its own formats, it doens't make sense to do something too generic.
 * Still, utility functions can be shared.
 */
export default class SplashCakeClient {
    /**
     * Hits the endpoint that downloads the given foreign image and pushes it into our cdn.
     * @param  {string} src
     * @return {Promise}
     */
    saveImageFromUrl(src, jcropSpecs) {
        return axios({
            url: `/events/saveImageFromUrl/${getEventId() || ''}`,
            ...METHOD_POST,
            // responseType:'json',
            headers: { ...CONTENT_TYPE_FORM },
            withCredentials: true,
            data: qs.stringify({
                src,
            }),
        })
            .then((response) => response.data)
            .then((response) => response.data.new_src);
    }

    /**
     * Hits the endpoint that performs a back-end crop of the given image by url.
     * @param  {string} src
     * @param  {object{x, y, width, height, [percentage]}} jcropSpecs
     * @return {Promise}
     */
    cropImage(src, jcropSpecs) {
        return axios({
            url: `/events/cropExistingImage/${getEventId() || ''}`,
            ...METHOD_POST,
            withCredentials: true,
            headers: {
                ...CONTENT_TYPE_FORM,
            },
            data: qs.stringify({
                src,
                c: jcropSpecs,
            }),
        })
            .then((response) => response.data)
            .then((response) => response.data);
    }

    uploadFile = uploadFile;

    duplicateEvent(data) {
        return axios({
            url: '/events/duplicate',
            ...METHOD_POST,
            withCredentials: true,
            headers: {
                ...CONTENT_TYPE_FORM,
            },
            data: qs.stringify({
                data,
            }),
        }).then((response) => response.data);
    }

    checkDomainAvailability(domain, eventId) {
        return axios({
            url: '/splash/checkDomainAvailability',
            ...METHOD_POST,
            withCredentials: true,
            headers: {
                ...CONTENT_TYPE_FORM,
            },
            data: qs.stringify({
                domain,
                event_id: eventId || getEventId(),
            }),
        })
            .then((response) => response.data)
            .then((response) => {
                const domainIsAvailable = !!response.available;

                if (domainIsAvailable) {
                    return domainIsAvailable;
                } else {
                    throw response;
                }
            });
    }

    getDeletedEmailTemplates(eventId) {
        return axios({
            url: `/api_email/template/${eventId}/deleted`,
            withCredentials: true,
        }).then((response) => response.data);
    }

    reviveEmailTemplate(eventId, eventMessageId) {
        return axios({
            url: `/api_email/template/${eventMessageId}/revive`,
            ...METHOD_POST,
            withCredentials: true,
            headers: { ...CONTENT_TYPE_FORM },
            data: qs.stringify({
                event_id: eventId,
            }),
        }).then((response) => response.data);
    }

    getOrgGroupStructure(organisationId) {
        return axios({
            url: `/api/groups/${organisationId}`,
        })
            .then((response) => response.data)
            .catch(catchSplajaxError);
    }

    /**
     * @param {string} eventId
     * @param {string} discountCode
     * @param {object} ticketQuantitiesByTicketTypeId
     * @return {Promise}
     */
    verifyDiscountCode(eventId, discountCode, ticketQuantitiesByTicketTypeId) {
        const url = flags.bool('isThirdPartyTicketCheckoutEnabled')
            ? `/purchase/checkout/apply-discount/${eventId}`
            : `/purchase/apply-discount/${eventId}`;

        return axios({
            url,
            ...METHOD_POST,
            withCredentials: true,
            headers: {
                ...CONTENT_TYPE_FORM,
            },
            data: qs.stringify({
                code: discountCode,
                tickets: ticketQuantitiesByTicketTypeId,
            }),
        })
            .then((response) => ({
                ticketOrder: get(response, 'data.data.ticket_order', {}),
                ticketSales: get(response, 'data.data.ticket_sale', []).map((ticketSale) => ({
                    id: get(ticketSale, 'id'),
                    name: get(ticketSale, 'ticket_type_name'),
                    quantity: get(ticketSale, 'quantity'),
                    price: get(ticketSale, 'price'),
                    fee: get(ticketSale, 'total_fee'),
                    total: get(ticketSale, 'total'),
                    ticketTypeId: get(ticketSale, 'ticket_type_id'),
                })),
            }))
            .catch((error) => {
                throw get(error, 'response.data.error');
            });
    }

    duplicateGroupStructure(destinationId, userId, sourceId) {
        return axios({
            url: `/api/groups/${destinationId}/duplicate/${sourceId}`,
            ...METHOD_POST,
            headers: { ...CONTENT_TYPE_FORM },
            body: qs.stringify({
                userId,
            }),
        })
            .then((response) => response.data)
            .catch(catchSplajaxError);
    }
}

export const getCakeClient = () => new SplashCakeClient();
